/**
 * G11 / V31 in the browser: routing rules and learned outcomes as encrypted `setting` entities
 * (synced to trusted devices; the hub never sees them), plus the "Auto" suggestion used by
 * New session.
 */

import { useQuery } from '@tanstack/react-query'
import {
  DEFAULT_ROUTING_PREFERENCES,
  RoutingCorrections,
  type RoutingHint,
  type RoutingOutcome,
  RoutingOutcomes,
  RoutingPreferences,
  RoutingRule,
  RoutingRunOutcome,
  routingRunEntityId,
  SettingEntry,
  settingEntityId,
} from '@viko/protocol'
import {
  decideRouting,
  explainRouting,
  machineMatches,
  type RoutingDecision,
  routingHintFor,
} from '@viko/scheduler'
import { useEffect } from 'react'
import { useBackend } from './backend.js'
import type { BrowserEntities } from './encrypted-entities.js'
import { isActiveTab } from './query-client.js'

type Entities = Pick<BrowserEntities, 'list' | 'write'>
type Name = 'routingPreferences' | 'routingOutcomes'

async function read<T>(
  entities: Entities,
  name: Name,
  parse: (v: unknown) => { success: boolean; data?: T },
): Promise<T | undefined> {
  const id = settingEntityId(name)
  const row = (await entities.list('setting')).find((r) => r.id === id)
  const entry = SettingEntry.safeParse(row?.value)
  if (!entry.success || entry.data.name !== name) return undefined
  const r = parse(entry.data.value)
  return r.success ? r.data : undefined
}

export const loadRoutingPreferences = async (entities: Entities): Promise<RoutingPreferences> =>
  (await read(entities, 'routingPreferences', (v) => RoutingPreferences.safeParse(v))) ??
  DEFAULT_ROUTING_PREFERENCES

export const loadRoutingOutcomes = async (
  entities: Entities,
): Promise<RoutingOutcomes | undefined> =>
  read(entities, 'routingOutcomes', (v) => RoutingOutcomes.safeParse(v))

export async function saveRoutingPreferences(
  entities: Entities,
  p: RoutingPreferences,
  now = Date.now(),
) {
  const value = RoutingPreferences.parse({ ...p, updatedAt: now })
  await entities.write(
    'setting',
    settingEntityId('routingPreferences'),
    SettingEntry.parse({ name: 'routingPreferences', value, updatedAt: now }),
  )
  return value
}

export async function saveRoutingOutcomes(
  entities: Entities,
  o: RoutingOutcomes,
  now = Date.now(),
) {
  const value = RoutingOutcomes.parse(o)
  await entities.write(
    'setting',
    settingEntityId('routingOutcomes'),
    SettingEntry.parse({ name: 'routingOutcomes', value, updatedAt: now }),
  )
  return value
}

/** Add or replace the rule for a task type (one global rule per type, one per project). */
export function upsertRule(p: RoutingPreferences, rule: RoutingRule): RoutingPreferences {
  const same = (r: RoutingRule) => r.taskType === rule.taskType && r.projectId === rule.projectId
  return { ...p, rules: [...p.rules.filter((r) => !same(r)), RoutingRule.parse(rule)] }
}

export const removeRule = (p: RoutingPreferences, id: string): RoutingPreferences => ({
  ...p,
  rules: p.rules.filter((r) => r.id !== id),
})

export function useRoutingPreferences() {
  const b = useBackend()
  const entities = b.entities
  return useQuery({
    queryKey: ['routing-preferences', b.artifactAccess?.accountId] as const,
    queryFn: async () =>
      entities ? await loadRoutingPreferences(entities) : DEFAULT_ROUTING_PREFERENCES,
    enabled: !!entities,
    retry: false,
  })
}

export function useRoutingOutcomes() {
  const b = useBackend()
  const entities = b.entities
  return useQuery({
    queryKey: ['routing-outcomes', b.artifactAccess?.accountId] as const,
    queryFn: async () => (entities ? await loadAllOutcomes(entities) : []),
    enabled: !!entities,
    retry: false,
  })
}

export interface AutoRoute {
  agent?: 'claude' | 'codex'
  machineId?: string
  explanation: string
  decision: RoutingDecision
}

/** New session "Auto": classify the prompt, apply rules > learned > defaults, and explain. */
export function suggestRoute(
  prompt: string,
  projectId: string | undefined,
  machines: readonly { id: string; name: string; online: boolean }[],
  cfg: Parameters<typeof decideRouting>[1],
  now = Date.now(),
): AutoRoute | undefined {
  if (!prompt.trim()) return undefined
  const labels = /\b(image|icon|sprite|illustration)\b/i.test(prompt)
    ? (['image-gen'] as const)
    : /\bgoal[- ]loop\b/i.test(prompt)
      ? (['goal-loop'] as const)
      : ([] as const)
  const task = {
    id: 'draft',
    projectId: projectId ?? '',
    title: prompt.slice(0, 300),
    labels,
    priority: 1,
    readyAt: now,
    depsSatisfied: true,
    repo: '',
  }
  const decision = decideRouting(task, cfg, now)
  const agent =
    decision.agent?.order[0] ??
    (decision.taskType === 'image-generation' || decision.taskType === 'goal-loop'
      ? 'codex'
      : undefined)
  if (!agent) return undefined
  const wanted = decision.machine?.id
  const m = wanted
    ? machines.find((x) => x.online && machineMatches({ id: x.id, name: x.name } as never, wanted))
    : undefined
  const explanation = explainRouting(
    decision.agent
      ? decision
      : {
          ...decision,
          agent: {
            order: [agent, agent === 'claude' ? 'codex' : 'claude'],
            source: 'rule',
            note: `${decision.taskType.replace('-', ' ')} → ${agent}`,
          },
        },
    { agent, machine: m?.name ?? 'the selected machine', ...(m ? { machineId: m.id } : {}) },
  )
  return { agent, ...(m ? { machineId: m.id } : {}), explanation, decision }
}

// ---- per-run outcomes (daemon-written) and corrections (browser-written) ---------------------------

/**
 * Every outcome the account has: the daemon's per-run `routingOutcome` entities, the legacy
 * aggregated store, with the user's per-run corrections folded in. Malformed rows are skipped.
 */
export async function loadAllOutcomes(entities: Entities): Promise<RoutingOutcome[]> {
  const rows = await entities.list('setting')
  const corrections = new Map<string, number>()
  const out: (RoutingOutcome & { runId?: string })[] = []
  for (const row of rows) {
    const e = SettingEntry.safeParse(row.value)
    if (!e.success) continue
    if (e.data.name === 'routingCorrections') {
      const c = RoutingCorrections.safeParse(e.data.value)
      if (c.success)
        corrections.set(c.data.runId, (corrections.get(c.data.runId) ?? 0) + c.data.count)
    } else if (e.data.name === 'routingOutcome') {
      const o = RoutingRunOutcome.safeParse(e.data.value)
      if (o.success) out.push(o.data)
    } else if (e.data.name === 'routingOutcomes') {
      const o = RoutingOutcomes.safeParse(e.data.value)
      if (o.success) out.push(...o.data.outcomes)
    }
  }
  return out.map(({ runId, ...o }) => {
    const n = runId ? corrections.get(runId) : undefined
    return n ? { ...o, corrections: (o.corrections ?? 0) + n } : o
  })
}

/** The user redirected/undid/fixed up a run: count it against the run's provider. */
export async function recordCorrection(entities: Entities, runId: string, now = Date.now()) {
  const id = routingRunEntityId('routingCorrections', runId)
  const row = (await entities.list('setting')).find((r) => r.id === id)
  const prev = SettingEntry.safeParse(row?.value)
  const was =
    prev.success && prev.data.name === 'routingCorrections'
      ? RoutingCorrections.safeParse(prev.data.value)
      : undefined
  const value = RoutingCorrections.parse({
    runId,
    count: (was?.success ? was.data.count : 0) + 1,
    at: now,
  })
  await entities.write(
    'setting',
    id,
    SettingEntry.parse({ name: 'routingCorrections', value, updatedAt: now }),
  )
}

// ---- hints for the hub scheduler ------------------------------------------------------------------

interface HintTask {
  id: string
  projectId: string
  title: string
  labels: string[]
  routeHint?: RoutingHint | null
}

const same = (a?: RoutingHint | null, b?: RoutingHint | null) =>
  a?.agent === b?.agent && a?.machine === b?.machine && a?.source === b?.source

/**
 * Hub tasks the scheduler will route need a content-free hint (agent/machine + source), because
 * the hub cannot read rules or history. Returns only the tasks whose hint must change.
 */
export function planHintUpdates(
  tasks: readonly HintTask[],
  cfg: Parameters<typeof decideRouting>[1],
  now: number,
): { id: string; hint: RoutingHint | null }[] {
  const out: { id: string; hint: RoutingHint | null }[] = []
  for (const t of tasks) {
    const hint =
      routingHintFor(
        {
          id: t.id,
          projectId: t.projectId,
          title: t.title,
          labels: t.labels as never,
          priority: 1,
          readyAt: now,
          depsSatisfied: true,
          repo: '',
        },
        cfg,
        now,
      ) ?? null
    if (!same(hint, t.routeHint)) out.push({ id: t.id, hint })
  }
  return out
}

/** Keep ready tasks' hints current while the app is open (hub-scheduled, plaintext-work accounts only). */
export function useRoutingHintSync(intervalMs = 60_000) {
  const b = useBackend()
  const prefs = useRoutingPreferences()
  const outcomes = useRoutingOutcomes()
  useEffect(() => {
    // Encrypted accounts keep tasks as sealed entities the hub scheduler cannot see.
    if (b.entities || !prefs.data) return
    let stopped = false
    const run = async () => {
      try {
        const ready = await b.api.tasks({ status: 'ready' })
        const updates = planHintUpdates(
          ready,
          { prefs: prefs.data, outcomes: outcomes.data },
          Date.now(),
        )
        for (const u of updates) {
          if (stopped) return
          await b.api.updateTask(u.id, { routeHint: u.hint })
        }
      } catch {
        /* the next tick retries; scheduling falls back to defaults meanwhile */
      }
    }
    void run()
    // Visible and focused only: a background tab never writes hints.
    const t = setInterval(() => {
      if (isActiveTab()) void run()
    }, intervalMs)
    return () => {
      stopped = true
      clearInterval(t)
    }
  }, [b, prefs.data, outcomes.data, intervalMs])
}
